import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { WorldMap } from "@components/world-map/world-map";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { CloseIcon, Dialog, IconButton, Skeleton, Text, TextField, useToast } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { SEARCH_DEBOUNCE_TIME } from "@hooks/use-debounced-search-text";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { fetchProjectDetails, updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { isGeocodeAbortError, reverseGeocode } from "@utils/geocoding-utils";
import { getProjectAddressType, isProjectAddressUnset } from "@utils/project-utils";
import { parseCoordinatesString, stringifyCoordinates } from "@utils/string-utils";
import { useDebounce } from "@utils/time-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { debounce } from "es-toolkit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UiUtils } from "@stellar/utils";

/** The input of the dialog that the user last used to set the project location */

/**
 * @returns A dialog that allows users to edit the location of a project.
 * The address field can be left empty.
 * The coordinates (position) field cannot be empty due to a backend requirement.
 */
export function ProjectLocationEditor({
  project,
  onClose
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const selectedProjectInStore = useAppSelector(selectedProjectSelector);
  const resolvedProject = selectedProjectInStore?.id === project.id ? selectedProjectInStore : project;
  const {
    canEditProjectDetails
  } = useHasUserValidPermissionProjectLevel({
    project: resolvedProject
  });
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(selectedProjectInStore?.id !== project.id);
  const initialCoordinates = stringifyCoordinates(project.position);
  const [coordinates, setCoordinates] = useState(initialCoordinates);
  const [coordinatesError, setCoordinatesError] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [geocodingAddressState, setGeocodingAddressState] = useState("uninitialized");
  const [address, setAddress] = useState(project.address);
  // postalAddress value is initialized as undefined because passing undefined in the backend
  // request to update the project leaves postalAddress unchanged, while passing null removes
  // the postalAddress value from the project.
  const [postalAddress, setPostalAddress] = useState(undefined);
  const [locationInputMethod, setLocationInputMethod] = useState("unchanged");
  // Whether the address field currently holds a value derived from the coordinates rather
  // than one the user provided. Only while it is derived may the coordinates field overwrite
  // the address, so an address typed or picked by the user is never lost.
  const [isAddressDerivedFromCoordinates, setIsAddressDerivedFromCoordinates] = useState(() => isProjectAddressUnset(project.address));
  const [reverseGeocodingState, setReverseGeocodingState] = useState("uninitialized");
  const reverseGeocodeAbort = useRef(null);
  const mapRef = useRef(null);
  const isLocationEditable = !isCheckingPermissions && canEditProjectDetails;

  // Projects in map/list endpoints can be loaded without permissions.
  // Fetch full project details on dialog open so permission checks are accurate.
  useEffect(() => {
    if (selectedProjectInStore?.id === project.id) {
      setIsCheckingPermissions(false);
      return;
    }
    async function fetchDetails() {
      setIsCheckingPermissions(true);
      try {
        await dispatch(fetchProjectDetails({
          companyId,
          projectId: project.id
        })).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `ProjectLocationEditor-fetchDetails-${Date.now().toString()}`,
          title: "Could not load project permissions",
          error
        });
      } finally {
        setIsCheckingPermissions(false);
      }
    }
    void fetchDetails();
  }, [companyId, dispatch, handleErrorWithToast, project.id, selectedProjectInStore?.id]);
  const debouncedCoordinates = useDebounce(coordinates, SEARCH_DEBOUNCE_TIME);
  // Debounce position changes before passing it to the map component
  // to avoid the map from flickering while the user is typing.
  // The debounced position needs to be memoized, otherwise any changes in
  // the parent component state would cause the map entities-layer to be recreated,
  // causing a visible flicker in the map.
  const debouncePosition = useMemo(() => {
    return parseCoordinatesString(debouncedCoordinates);
  }, [debouncedCoordinates]);

  /**
   * World map entities array with a single entity that represent the current GPS coordinates.
   * If the coordinates input is empty then the array is empty so that no marker is shown on the map.
   * The array is memoized to keep the same array reference and avoid a flickering in the map
   * any time that the array value is re-computed.
   */
  const entities = useMemo(() => {
    return debouncePosition ? [{
      ...project,
      position: debouncePosition
    }] : [];
  }, [project, debouncePosition]);

  /**
   * Moves the map to the given coordinates if they are valid.
   * @param value The coordinates string to go to
   */
  function goToCoordinates(value) {
    const parsedCoordinates = parseCoordinatesString(value);
    if (parsedCoordinates) {
      mapRef.current?.goTo(parsedCoordinates);
    }
  }

  /**
   * Handles updates to the address input field.
   * It marks the dialog as changed and it updates the address state.
   * It deliberately reads no state, so that it stays referentially stable and the
   * debounced reverse geocode below can keep its timer across re-renders.
   * @param newAddress The new address input value
   */
  const updateAddressField = useCallback(newAddress => {
    setHasChanges(true);
    setAddress(newAddress);
  }, []);

  /**
   * Handles updates to the coordinates input field.
   * If there was an error it resets it, and it marks the dialog as changed.
   * @param value The new coordinates input value
   */
  function updateCoordinatesField(value_0) {
    setCoordinatesError("");
    setHasChanges(true);
    setCoordinates(value_0);
  }

  /**
   * Handles setting a custom address field.
   * If the new value is a valid coordinates string, it sets the coordinates field to
   * that value, otherwise it leaves the coordinates field unchanged.
   * It also moves the map to the new coordinates.
   * @param input The new address input value
   */
  function onAddressInputChange(input) {
    if (!isLocationEditable) {
      return;
    }
    updateAddressField(input);
    setLocationInputMethod("typed address");
    // An address typed by the user is theirs to keep, so the coordinates field must not
    // overwrite it. Emptying the field hands ownership back, which lets a user who clears
    // their address and then types coordinates get a reverse geocoded one.
    const isDerived = isProjectAddressUnset(input);
    setIsAddressDerivedFromCoordinates(isDerived);
    if (!isDerived) {
      // A reverse geocode scheduled moments ago by the coordinates field would still land
      // on top of what the user is typing here, so it has to go.
      cancelPendingAddressSync();
    }
    const trimmedInput = input.trim();

    // Whenever a custom address is set, we need to unset the postal address in the backend.
    // To unset the value via the update request we must set the postal address value to null.
    setPostalAddress(null);
    const position = parseCoordinatesString(trimmedInput);
    if (!position) {
      return;
    }
    updateCoordinatesField(trimmedInput);
    goToCoordinates(trimmedInput);
  }

  /**
   * Handles the selection of a geocoded address.
   * Sets address, position and postal address.
   * It also moves the map to the new coordinates.
   * @param geocodedAddress The selected geocoded address
   */
  function onGeocodedAddressSelect(geocodedAddress) {
    if (!isLocationEditable) {
      return;
    }
    updateAddressField(geocodedAddress.address);
    setLocationInputMethod("address suggestion");
    // The user picked this address themselves, so the coordinates field must not overwrite
    // it, neither now nor through a reverse geocode it scheduled a moment ago.
    setIsAddressDerivedFromCoordinates(false);
    cancelPendingAddressSync();
    setPostalAddress(geocodedAddress.postalAddress);
    const positionString = stringifyCoordinates(geocodedAddress.position);
    updateCoordinatesField(positionString);
    goToCoordinates(positionString);
  }

  /**
   * Handles clicks on the map that are not on a marker or cluster.
   * It extracts the GPS coordinates from the clicked position, then it reverse geocodes them
   * to get a human-readable address, and finally it sets the address, position and postal address fields.
   * @param event The map click event
   */
  async function onMapClick(event) {
    if (!isLocationEditable) {
      return;
    }
    const {
      mapPoint
    } = event;
    const lat = mapPoint.latitude;
    const lng = mapPoint.longitude;

    // The click supersedes anything the coordinates field scheduled a moment ago.
    cancelPendingAddressSync();
    try {
      if (typeof lat !== "number" || isNaN(lat) || typeof lng !== "number" || isNaN(lng)) {
        throw new Error("Failed to extract coordinates from map click event");
      }
      const {
        address: address_0,
        postalAddress: postalAddress_0
      } = await reverseGeocode({
        lat,
        lng
      });
      const positionString_0 = stringifyCoordinates({
        lat,
        lng
      });
      updateAddressField(address_0);
      setLocationInputMethod("map click");
      // A map click address is itself reverse geocoded from a position, so the coordinates
      // field keeps ownership and a later manual tweak of them updates the address again.
      setIsAddressDerivedFromCoordinates(true);
      setPostalAddress(postalAddress_0);
      updateCoordinatesField(positionString_0);
      trackSetLocationOnMap("succeeded");
    } catch (error_0) {
      trackSetLocationOnMap("failed");
      handleErrorWithToast({
        id: `onMapClick-${Date.now().toString()}`,
        title: "Failed to set a location from a map click",
        error: error_0
      });
    }
  }

  /** Tracks a map click used to set the location, whether or not the user confirms it afterwards. */
  function trackSetLocationOnMap(outcome) {
    trackEvent({
      name: ProjectEvents.setProjectLocationOnMap,
      props: {
        outcome,
        hasExistingLocation: Boolean(project.position)
      }
    });
  }

  /**
   * Reverse geocodes the given position and writes the result into the address field.
   * Any request still in flight is aborted first, so that a slow older response cannot
   * land after a newer one and leave a stale address behind.
   * @param position The GPS position to get an address for
   */
  const syncAddressToCoordinates = useCallback(async position_0 => {
    try {
      reverseGeocodeAbort.current?.abort();
      reverseGeocodeAbort.current = new AbortController();
      const {
        address: address_1,
        postalAddress: postalAddress_1
      } = await reverseGeocode(position_0, reverseGeocodeAbort.current.signal);
      updateAddressField(address_1);
      setPostalAddress(postalAddress_1);
      setReverseGeocodingState("succeeded");
    } catch (error_1) {
      // An aborted request was superseded, either by a newer one that now owns the
      // pending state or by the user clearing the field. Either way the caller has
      // already set the state it wants, so leave it alone.
      if (isGeocodeAbortError(error_1)) {
        return;
      }
      handleErrorWithToast({
        id: `syncAddressToCoordinates-${Date.now().toString()}`,
        title: "Failed to get an address from the coordinates",
        error: error_1
      });
      setReverseGeocodingState("rejected");
    }
  }, [handleErrorWithToast, updateAddressField]);

  // Debounce the reverse geocoding so that fast typing in the coordinates field issues
  // one request per pause instead of one per keystroke. The debounced function has to be
  // memoized, otherwise a re-render would replace it and drop its pending timer.
  const scheduleAddressSync = useMemo(() => debounce(position_1 => {
    void syncAddressToCoordinates(position_1);
  }, SEARCH_DEBOUNCE_TIME), [syncAddressToCoordinates]);

  // Drop any scheduled and in-flight reverse geocoding when the dialog closes.
  useEffect(() => {
    return () => {
      scheduleAddressSync.cancel();
      reverseGeocodeAbort.current?.abort();
    };
  }, [scheduleAddressSync]);

  /**
   * Drops any scheduled and in-flight reverse geocoding, so that a late response
   * cannot fill the address field after the user has moved on.
   */
  function cancelPendingAddressSync() {
    scheduleAddressSync.cancel();
    reverseGeocodeAbort.current?.abort();
    setReverseGeocodingState("uninitialized");
  }

  /**
   * Handles a coordinates value typed by the user. When the address field holds a value
   * derived from the coordinates, it keeps that address in sync with them.
   * This is only called from the coordinates input, so the coordinates set programmatically
   * by the address field or by a map click never trigger a reverse geocode.
   * @param value The new coordinates input value
   */
  function onCoordinatesInputChange(value_1) {
    setLocationInputMethod("typed coordinates");
    updateCoordinatesField(value_1);
    if (!isAddressDerivedFromCoordinates) {
      return;
    }
    const position_2 = parseCoordinatesString(value_1);
    if (!position_2) {
      cancelPendingAddressSync();

      // Emptying the field removes the source of the derived address, so the address goes
      // with it. An invalid value is only a transient typing state and leaves the address
      // untouched, as the user cannot confirm the dialog until they fix or clear the field.
      if (value_1.trim().length === 0) {
        updateAddressField("");
        setPostalAddress(null);
      }
      return;
    }

    // Mark it as pending straight away, so that the dialog is already disabled during the
    // debounce interval and a fast user cannot confirm before the address arrives.
    setReverseGeocodingState("pending");
    scheduleAddressSync(position_2);
  }

  /** Issues the request to update the address and coordinates */
  async function onConfirm() {
    if (!isLocationEditable) {
      return;
    }
    const updatedAddress = address.trim() || UiUtils.DASH;
    let updatedPosition;

    // If the coordinates input is empty then assign null to the position to unset it in the backend.
    // If the coordinates input has an invalid value then set the error state and do an early exit.
    // If the coordinates input has a valid value then update the position with it.
    const sanitizedCoordinates = coordinates.trim();
    if (sanitizedCoordinates.length === 0) {
      updatedPosition = null;
    } else {
      const parsedCoordinates_0 = parseCoordinatesString(sanitizedCoordinates);
      if (parsedCoordinates_0 === null) {
        return setCoordinatesError("Invalid coordinates");
      } else {
        updatedPosition = parsedCoordinates_0;
      }
    }
    setIsUpdating(true);
    try {
      await dispatch(updateProjectDetails({
        projectId: project.id,
        payload: {
          address: updatedAddress,
          position: updatedPosition,
          postalAddress
        }
      })).unwrap();
      openToast({
        title: "Project location updated successfully."
      });
      trackEvent({
        name: ProjectEvents.updateProjectLocation,
        props: {
          addressType: updatedAddress === project.address ? "unchanged" : getProjectAddressType({
            address: updatedAddress,
            postalAddress,
            emptyAddressType: "removed"
          }),
          isGpsPositionSet: Boolean(updatedPosition),
          locationInputMethod
        }
      });
      onClose();
    } catch (_) {
      // Do nothing as the error is already handled by the error slice.
    } finally {
      setIsUpdating(false);
    }
  }
  return <Dialog open={true} onCancel={onClose} onConfirm={onConfirm} isConfirmDisabled={!hasChanges || !isLocationEditable} showSpinner={isUpdating} disabled={isUpdating || geocodingAddressState === "pending" || reverseGeocodingState === "pending"} confirmText="Update" title="Update project location">
      <Text variant="labelM" sx={{
      mb: "8px"
    }}>
        Address
      </Text>
      {isCheckingPermissions ? <Skeleton variant="rounded" height={40} sx={{
      mb: "24px"
    }} /> : <AddressAutocomplete input={address} setInput={onAddressInputChange} onGeocodedAddressSelect={onGeocodedAddressSelect} onLoadingGeocodedAddressChange={setGeocodingAddressState} isDisabled={!isLocationEditable} sx={{
      mb: "24px"
    }} />}
      {isLocationEditable && <Text variant="helpText" sx={{
      mb: "8px"
    }}>
          You can also click on the map to set a location
        </Text>}
      <WorldMap mapRef={mapRef} entities={entities} mapContext="location editor" onMapClick={isLocationEditable ? onMapClick : undefined} shouldShowMapControls sx={{
      height: "300px",
      marginBottom: "24px",
      cursor: isLocationEditable ? "crosshair" : "default"
    }} />
      {isCheckingPermissions ? <Skeleton variant="rounded" height={57} /> : <>
          <TextField label={<Text variant="labelM">Coordinates</Text>} placeholder="Latitude, Longitude" fullWidth onTextChanged={onCoordinatesInputChange} error={coordinatesError} text={coordinates} readOnly={!isLocationEditable} endAdornment={isLocationEditable && coordinates.length > 0 ? <IconButton size="s" margin={0.5} onClick={() => onCoordinatesInputChange("")}>
                  <CloseIcon />
                </IconButton> : undefined} />
          {!isLocationEditable && <Text variant="helpText" sx={{
        mt: "8px",
        color: "error.main"
      }}>
              You don't have permissions to edit the project location.
            </Text>}
        </>}
    </Dialog>;
}